import { useShopwareContext } from '@shopware/composables'
import type { Schemas } from '#shopware'

/**
 * Composable for handling newsletter subscriptions during checkout
 */
export function useNewsletterSubscription() {
    const { apiClient } = useShopwareContext()

    // Hardcoded salutation IDs (same as NewsletterSubscribe.vue)
    const salutations = {
        male: '617bcb31405d45348b59605978a0fc57',
        female: 'dec4113841954692ab3373dfad3a46fb'
    }

    /**
     * Get salutation ID from user or address data
     */
    function getSalutationId(user?: Schemas['Customer'] | null, address?: Schemas['CustomerAddress'] | null): string | null {
        // Try user salutation first
        if (user?.salutationId) {
            return user.salutationId
        }

        // Try user default billing address
        if (user?.defaultBillingAddress?.salutationId) {
            return user.defaultBillingAddress.salutationId
        }

        // Try provided address
        if (address?.salutationId) {
            return address.salutationId
        }

        return null
    }

    /**
     * Get gender number (1=male, 2=female) from salutation ID
     */
    function getGenderFromSalutationId(salutationId: string): number {
        return salutationId === salutations.male ? 1 : 2
    }

    /**
     * Subscribe user to newsletter
     * @param email - User email address
     * @param salutationId - Shopware salutation ID (required)
     * @param firstName - Optional first name
     * @param lastName - Optional last name
     */
    async function subscribeToNewsletter(
        email: string,
        salutationId: string,
        firstName?: string,
        lastName?: string
    ): Promise<void> {
        const gender = getGenderFromSalutationId(salutationId)

        const payload = {
            email,
            salutationId,
            option: 'subscribe' as const,
            storefrontUrl: import.meta.client ? window.location.origin : '',
            customFields: {
                a4g_brevo_newsletter_gender: gender
            },
            ...(firstName && { firstName }),
            ...(lastName && { lastName })
        }

        await apiClient.invoke('subscribeToNewsletter post /newsletter/subscribe', {
            body: payload
        })
    }

    return {
        getSalutationId,
        subscribeToNewsletter,
        salutations
    }
}
